import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const emptyForm = { first_name: "", last_name: "", email: "", whatsapp: "", notes: "" };

export const BookingModal = () => {
  const { isOpen, presetServiceId, closeBooking } = useBooking();
  const { T, lang } = useLanguage();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (isOpen) {
      api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
      setServiceId(presetServiceId);
      if (presetServiceId) setStep(2);
      else setStep(1);
    } else {
      setStep(1); setServiceId(null); setDate(""); setSlots([]); setSlot(null);
      setForm(emptyForm); setResult(null);
    }
  }, [isOpen, presetServiceId]);

  useEffect(() => {
    if (date) {
      setLoadingSlots(true);
      setSlot(null);
      api.get(`/public/availability/slots?date=${date}`)
        .then((r) => setSlots(r.data))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [date]);

  const selectedService = services.find((s) => s.id === serviceId);
  const minDate = new Date().toISOString().split("T")[0];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/appointments", {
        service_id: serviceId, date, slot, client: form, notes: form.notes, lang,
      });
      setResult(data);
      setStep(4);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && closeBooking()}>
      <DialogContent title={T.booking.title} className="max-w-lg bg-[#F7F0E8] border-none rounded-none p-0 overflow-hidden" data-testid="booking-modal">
        <div className="bg-[#9B6179] px-7 py-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#E8C47A]">{T.booking.title}</p>
          {step < 4 && <p className="font-serif text-xl text-[#F7F0E8] mt-1">{T.booking[`step${step}`]}</p>}
        </div>
        <div className="p-7 max-h-[70vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-1" data-testid="booking-step-service">
              {services.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`booking-service-option-${s.slug}`}
                  onClick={() => { setServiceId(s.id); setStep(2); }}
                  className={`w-full text-left px-4 py-3.5 border-b border-[#090708]/8 transition-colors flex items-center justify-between ${serviceId === s.id ? "bg-[#090708]/5" : "hover:bg-[#090708]/5"}`}
                >
                  <span className="font-serif text-lg text-[#090708]">{lang === "pt" ? s.name_pt : s.name_fr}</span>
                  <span className="text-[10px] tracking-widest uppercase text-[#090708]/40">2h</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="booking-step-date">
              {!presetServiceId && (
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[11px] tracking-widest uppercase text-[#090708]/50" data-testid="booking-back-to-service">
                  <ChevronLeft className="w-3 h-3" /> {T.booking.back}
                </button>
              )}
              {selectedService && <p className="font-serif text-lg text-[#090708]">{lang === "pt" ? selectedService.name_pt : selectedService.name_fr}</p>}
              <div>
                <Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.select_date}</Label>
                <Input type="date" min={minDate} value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-date-input" />
              </div>
              {date && (
                <div>
                  <Label className="mb-2 block text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.select_slot}</Label>
                  {loadingSlots ? (
                    <p className="text-sm text-[#090708]/50">{T.common.loading}</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-[#090708]/50" data-testid="booking-no-slots">{T.booking.no_slots}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s.slot}
                          disabled={!s.available}
                          onClick={() => setSlot(s.slot)}
                          data-testid={`booking-slot-${s.slot}`}
                          className={`rounded-[2px] px-3 py-2.5 text-sm border transition-colors ${!s.available ? "opacity-30 cursor-not-allowed border-[#090708]/10" : slot === s.slot ? "bg-[#090708] text-[#E8C47A] border-[#090708]" : "border-[#090708]/20 hover:border-[#090708]"}`}
                        >
                          {s.slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button
                disabled={!slot}
                onClick={() => setStep(3)}
                className="w-full rounded-[2px] bg-[#090708] text-[#E8C47A] py-3.5 text-[11px] tracking-[0.2em] uppercase disabled:opacity-30 transition-opacity"
                data-testid="booking-next-to-info"
              >
                {T.booking.next}
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="booking-step-info">
              <button onClick={() => setStep(2)} className="flex items-center gap-1 text-[11px] tracking-widest uppercase text-[#090708]/50" data-testid="booking-back-to-date">
                <ChevronLeft className="w-3 h-3" /> {T.booking.back}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.first_name}</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-input-firstname" /></div>
                <div><Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.last_name}</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-input-lastname" /></div>
              </div>
              <div><Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.email}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-input-email" /></div>
              <div><Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.whatsapp}</Label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+33..." className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-input-whatsapp" /></div>
              <div><Label className="text-[11px] tracking-widest uppercase text-[#090708]/60">{T.booking.notes}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-[#090708]/20 mt-1" data-testid="booking-input-notes" /></div>
              <button
                disabled={submitting || !form.first_name || !form.last_name || !form.email || !form.whatsapp}
                onClick={submit}
                className="w-full rounded-[2px] bg-[#090708] text-[#E8C47A] py-3.5 text-[11px] tracking-[0.2em] uppercase disabled:opacity-30 transition-opacity mt-2"
                data-testid="booking-submit-button"
              >
                {T.booking.confirm}
              </button>
            </div>
          )}

          {step === 4 && result && (
            <div className="text-center space-y-4 py-4" data-testid="booking-success">
              <CheckCircle2 className="w-10 h-10 text-[#090708] mx-auto" strokeWidth={1} />
              <p className="font-serif text-2xl text-[#090708]">{T.booking.success_title}</p>
              <p className="text-sm text-[#090708]/60 max-w-xs mx-auto">{T.booking.success_desc}</p>
              <a href={result.whatsapp_link} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-link" className="block">
                <button className="w-full bg-[#25D366] text-white py-3.5 text-[11px] tracking-[0.2em] uppercase">{T.booking.whatsapp_cta}</button>
              </a>
              <button onClick={closeBooking} className="w-full border border-[#090708]/20 py-3.5 text-[11px] tracking-[0.2em] uppercase" data-testid="booking-close-button">{T.booking.close}</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
